// ==============================================
// UrbanBrew App.js — Clean Production Version
// ==============================================
import React, { Suspense, useEffect, useState } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import GlobalContext from "./Context/Context";
import ScrollToTopButton from "./Components/ScrollToTop";
import LeftMenuModern from "./Components/Header/LeftMenuModern";
import FixedBanner from "./Components/FixedBanner/FixedBanner";
import PageLoader from "./Components/PageLoader";
import PageSeo from "./Components/PageSeo";
import AnalyticsRouteTracker from "./Components/AnalyticsRouteTracker";

// Home page stays eager-loaded for fastest first paint
import HomePage from "./Pages/Home/HomePage";

// Solutions — lazy-loaded
const BeanToCup = React.lazy(() => import("./Pages/Solutions/BeanToCup"));
const InstantMachines = React.lazy(() => import("./Pages/Solutions/InstantMachines"));
const SnackBeverageVending = React.lazy(() => import("./Pages/Solutions/SnackBeverageVending"));
const BeverageEssentials = React.lazy(() => import("./Pages/Solutions/BeverageEssentials"));
const SmartTray = React.lazy(() => import("./Pages/Solutions/SmartTray"));
const RefillSubscriptions = React.lazy(() => import("./Pages/Solutions/RefillSubscriptions"));
const BrewBetter = React.lazy(() => import("./Pages/Solutions/BrewBetter"));
const ServeSmarter = React.lazy(() => import("./Pages/Solutions/ServeSmarter"));
const SelfServe = React.lazy(() => import("./Pages/Solutions/SelfServe"));

// Articles
const ArticlesList = React.lazy(() => import("./Pages/Articles/ArticlesList"));
const ArticleDetail = React.lazy(() => import("./Pages/Articles/ArticleDetail"));

// Contact & Company
const ContactPage = React.lazy(() => import("./Pages/Contact/ContactPage"));
const CareersPage = React.lazy(() => import("./Pages/Careers/CareersPage"));

// Tools
const EstimationTool = React.lazy(() => import("./Pages/Tools/EstimationTool"));
const OnboardingForm = React.lazy(() => import("./Pages/Tools/OnboardingForm"));
const SLASelector = React.lazy(() => import("./Pages/Tools/SLASelector"));
const MachineAdvisor = React.lazy(() => import("./Pages/Tools/MachineAdvisor"));

// Legal / Error
const PrivacyPolicy = React.lazy(() => import("./Pages/Legal/PrivacyPolicy"));
const TermsConditions = React.lazy(() => import("./Pages/Legal/TermsConditions"));
const CookiesPolicy = React.lazy(() => import("./Pages/Legal/CookiesPolicy"));
const NotFound = React.lazy(() => import("./Pages/Error/NotFound"));

function App() {
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customModal, setCustomModal] = useState({ el: null, isOpen: false });
  const location = useLocation();

  // Layout utilities — run immediately on route change
  useEffect(() => {
    let cancelled = false;

    const runUtilities = async () => {
      const utils = await import("./Functions/Utilities");
      if (cancelled) return;
      utils.SetHeaderMenuPos();
      utils.setDocumentFullHeight();
    };

    runUtilities();

    return () => {
      cancelled = true;
    };
  }, [location]);

  // Disable scroll when modal is open
  useEffect(() => {
    document.body.classList.toggle("overflow-hidden", isModalOpen);
  }, [isModalOpen]);

  // Reset scroll and modals on route change
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    setFooterHeight(0);
    setCustomModal({ el: null, isOpen: false });
  }, [location]);

  return (
    <GlobalContext.Provider
      value={{
        headerHeight,
        setHeaderHeight,
        footerHeight,
        setFooterHeight,
        isModalOpen,
        setIsModalOpen,
        customModal,
        setCustomModal,
      }}
    >
      <PageSeo />
      <AnalyticsRouteTracker />
      <div className="App" style={{ "--header-height": `${headerHeight}px` }}>
        <LeftMenuModern />

        {location.pathname !== "/contact" && (
          <FixedBanner text="Let's get started" link="/contact" />
        )}

        <main
          className="overflow-x-hidden w-full max-w-full min-w-0 pr-14 md:pr-0"
          style={{ marginTop: headerHeight, marginBottom: footerHeight }}
        >
          <ScrollToTopButton />
          <Suspense fallback={<PageLoader />}>
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<HomePage />} />

              <Route path="/solutions/brew-better" element={<BrewBetter />} />
              <Route path="/solutions/serve-smarter" element={<ServeSmarter />} />
              <Route path="/solutions/self-serve" element={<SelfServe />} />
              <Route path="/solutions/bean-to-cup" element={<BeanToCup />} />
              <Route path="/solutions/instant-machines" element={<InstantMachines />} />
              <Route path="/solutions/SnackBeverageVending" element={<SnackBeverageVending />} />
              <Route path="/solutions/BeverageEssentials" element={<BeverageEssentials />} />
              <Route path="/solutions/smart-tray" element={<SmartTray />} />
              <Route path="/solutions/refill-subscriptions" element={<RefillSubscriptions />} />

              {/* Legacy kebab-case aliases → preserve indexed PascalCase URLs */}
              <Route
                path="/solutions/snack-beverage-vending"
                element={<Navigate to="/solutions/SnackBeverageVending" replace />}
              />
              <Route
                path="/solutions/beverage-essentials"
                element={<Navigate to="/solutions/BeverageEssentials" replace />}
              />

              <Route path="/articles" element={<ArticlesList />} />
              <Route path="/articles/:slug" element={<ArticleDetail />} />

              <Route path="/contact" element={<ContactPage />} />
              <Route path="/careers" element={<CareersPage />} />

              <Route path="/tools/estimator" element={<EstimationTool />} />
              <Route path="/tools/onboarding" element={<OnboardingForm />} />
              <Route path="/tools/sla" element={<SLASelector />} />
              <Route path="/tools/advisor" element={<MachineAdvisor />} />

              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsConditions />} />
              <Route path="/cookies" element={<CookiesPolicy />} />

              {/* Legal path aliases — same content, canonical tags point to primary URLs */}
              <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/legal/terms-and-conditions" element={<TermsConditions />} />
              <Route path="/legal/cookies-policy" element={<CookiesPolicy />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </GlobalContext.Provider>
  );
}

export default App;
